import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { KeyRound, Wand2, RefreshCw, Copy } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Slider } from "@/components/ui/slider";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { useLang } from "@/lib/i18n";

const CHARSETS = {
  upper: "ABCDEFGHJKLMNPQRSTUVWXYZ", // no I,O
  lower: "abcdefghijkmnpqrstuvwxyz", // no l,o
  digits: "23456789",                 // no 0,1
  symbols: "!@#$%^&*_-+=?",
};

// Password generator using WebCrypto (secure)
function generatePassword({ length, upper, lower, digits, symbols }) {
  const sets = [];
  if (upper) sets.push(CHARSETS.upper);
  if (lower) sets.push(CHARSETS.lower);
  if (digits) sets.push(CHARSETS.digits);
  if (symbols) sets.push(CHARSETS.symbols);
  if (sets.length === 0) sets.push(CHARSETS.lower);
  const pool = sets.join("");
  const out = new Array(length);
  const rand = new Uint32Array(length);
  crypto.getRandomValues(rand);
  const guaranteed = sets.map((s) => s[rand[0] % s.length]);
  for (let i = 0; i < length; i++) out[i] = pool[rand[i] % pool.length];
  for (let i = 0; i < guaranteed.length && i < length; i++) {
    const pos = rand[(i + 1) % length] % length;
    out[pos] = guaranteed[i];
  }
  return out.join("");
}

const CHAR_OPTIONS = [
  { key: "upper", label: "A-Z", testid: "pw-opt-upper" },
  { key: "lower", label: "a-z", testid: "pw-opt-lower" },
  { key: "digits", label: "0-9", testid: "pw-opt-digits" },
  { key: "symbols", label: "!@#$", testid: "pw-opt-symbols" },
];

const INITIAL_OPTS = { length: 20, upper: true, lower: true, digits: true, symbols: true };

export default function PasswordGeneratorPopover({ textareaRef, secret, setSecret }) {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [length, setLength] = useState(INITIAL_OPTS.length);
  const [upper, setUpper] = useState(INITIAL_OPTS.upper);
  const [lower, setLower] = useState(INITIAL_OPTS.lower);
  const [digits, setDigits] = useState(INITIAL_OPTS.digits);
  const [symbols, setSymbols] = useState(INITIAL_OPTS.symbols);
  const [preview, setPreview] = useState(() => generatePassword(INITIAL_OPTS));

  const regenerate = useCallback(() => {
    setPreview(generatePassword({ length, upper, lower, digits, symbols }));
  }, [length, upper, lower, digits, symbols]);

  useEffect(() => {
    regenerate();
  }, [regenerate]);

  const setterFor = (key) => ({
    upper: setUpper, lower: setLower, digits: setDigits, symbols: setSymbols,
  }[key]);
  const stateFor = (key) => ({ upper, lower, digits, symbols }[key]);

  const insertIntoTextarea = () => {
    const ta = textareaRef.current;
    const pw = preview;
    if (!ta) {
      setSecret((s) => s + pw);
    } else {
      const start = ta.selectionStart ?? secret.length;
      const end = ta.selectionEnd ?? secret.length;
      const next = secret.slice(0, start) + pw + secret.slice(end);
      setSecret(next);
      requestAnimationFrame(() => {
        ta.focus();
        const pos = start + pw.length;
        try {
          ta.setSelectionRange(pos, pos);
        } catch (err) {
          if (process.env.NODE_ENV !== "production") {
            console.warn("[pw-generator] setSelectionRange failed:", err);
          }
        }
      });
    }
    setOpen(false);
    toast.success(t("pwInserted"));
  };

  const copyPreview = async () => {
    try {
      await navigator.clipboard.writeText(preview);
      toast.success(t("copied"));
    } catch (err) {
      if (process.env.NODE_ENV !== "production") {
        console.warn("[pw-generator] clipboard write failed:", err);
      }
      toast.error("Copy failed");
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="pw-generator-button"
          className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-neutral-600 dark:text-neutral-400 hover:text-[#00ff66] border border-black/10 dark:border-white/10 hover:border-[#00ff66]/60 px-2.5 py-1.5 transition-colors"
        >
          <Wand2 className="w-3 h-3" /> {t("pwGenerator")}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={8}
        className="w-80 bg-white dark:bg-[#0a0a0a] border border-black/15 dark:border-white/15 rounded-none p-0 text-neutral-900 dark:text-neutral-100"
        data-testid="pw-generator-popover"
      >
        <div className="border-b border-black/10 dark:border-white/10 px-4 py-3 flex items-center gap-2">
          <KeyRound className="w-3.5 h-3.5 text-[#00ff66]" />
          <span className="text-[10px] font-mono uppercase tracking-widest text-neutral-600 dark:text-neutral-400">
            {t("pwGenerator")}
          </span>
        </div>
        <div className="p-4 space-y-4">
          <div className="border border-black/10 dark:border-white/10 bg-white dark:bg-black p-3 flex items-center gap-2">
            <code
              data-testid="pw-preview"
              className="flex-1 font-mono text-sm text-[#00ff66] break-all select-all"
            >
              {preview}
            </code>
            <button
              type="button"
              onClick={regenerate}
              data-testid="pw-regenerate"
              className="p-1.5 text-neutral-600 dark:text-neutral-400 hover:text-black dark:hover:text-white border border-black/10 dark:border-white/10 hover:border-black/40 dark:hover:border-white/40"
              title="Regenerate"
            >
              <RefreshCw className="w-3.5 h-3.5" />
            </button>
            <button
              type="button"
              onClick={copyPreview}
              data-testid="pw-copy"
              className="p-1.5 text-neutral-600 dark:text-neutral-400 hover:text-black dark:hover:text-white border border-black/10 dark:border-white/10 hover:border-black/40 dark:hover:border-white/40"
              title="Copy"
            >
              <Copy className="w-3.5 h-3.5" />
            </button>
          </div>

          <PwLengthSlider length={length} setLength={setLength} />

          <div className="grid grid-cols-2 gap-2 text-xs">
            {CHAR_OPTIONS.map((opt) => (
              <label
                key={opt.key}
                className="flex items-center gap-2 border border-black/10 dark:border-white/10 px-3 py-2 cursor-pointer hover:border-black/30 dark:hover:border-white/30"
              >
                <Checkbox
                  data-testid={opt.testid}
                  checked={stateFor(opt.key)}
                  onCheckedChange={(v) => setterFor(opt.key)(!!v)}
                />
                <span className="font-mono">{opt.label}</span>
              </label>
            ))}
          </div>

          <Button
            type="button"
            data-testid="pw-insert-button"
            onClick={insertIntoTextarea}
            className="w-full h-10 rounded-none bg-black dark:bg-white text-white dark:text-black hover:bg-[#00ff66] font-bold uppercase tracking-widest text-[11px]"
          >
            {t("pwInsert")}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function PwLengthSlider({ length, setLength }) {
  const { t } = useLang();
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">
          {t("pwLength")}
        </span>
        <span data-testid="pw-length-value" className="font-mono text-xs text-neutral-900 dark:text-white">
          {length}
        </span>
      </div>
      <Slider
        data-testid="pw-length-slider"
        min={8}
        max={64}
        step={1}
        value={[length]}
        onValueChange={(v) => setLength(v[0])}
      />
    </div>
  );
}
